import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { api, query } from "./client";
import type {
  AIConfig,
  AIConfigUpdate,
  AIConfigVersion,
  AlertEvent,
  Catalog,
  Channel,
  ChannelLookup,
  KeyInfo,
  Me,
  NotificationSettings,
  Overview,
  Paginated,
  Platform,
  PlatformKind,
  PublishJob,
  Schedule,
  Summary,
  SummaryDetail,
  SummarySources,
  TelegramAccount,
  TestPromptResult,
  Usage,
  WhatsAppExport,
} from "./types";

function useInvalidate() {
  const client = useQueryClient();
  return (...keys: QueryKey[]) =>
    Promise.all(keys.map((key) => client.invalidateQueries({ queryKey: key })));
}

// --- Auth -------------------------------------------------------------------

export const useMe = () =>
  useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("auth/me"),
    retry: false,
    staleTime: 5 * 60_000,
  });

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { username: string; password: string }) => {
      await api("auth/csrf");
      return api<Me>("auth/login", { method: "POST", body });
    },
    onSuccess: (me) => client.setQueryData(["me"], me),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api("auth/logout", { method: "POST" }),
    onSuccess: () => {
      client.clear();
      client.setQueryData(["me"], null);
    },
  });
}

// --- Overview ---------------------------------------------------------------

export const useOverview = () =>
  useQuery({
    queryKey: ["overview"],
    queryFn: () => api<Overview>("overview"),
    refetchInterval: 60_000,
  });

// --- Summaries ----------------------------------------------------------------

export interface SummaryFilters {
  status?: string;
  flagged?: boolean;
  kind?: string;
  q?: string;
  page?: number;
}

export const useSummaries = (filters: SummaryFilters) =>
  useQuery({
    queryKey: ["summaries", filters],
    queryFn: () =>
      api<Paginated<Summary>>(
        `summaries${query({ ...filters, flagged: filters.flagged ? 1 : undefined })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

export const useSummary = (id: number | null) =>
  useQuery({
    queryKey: ["summary", id],
    queryFn: () => api<SummaryDetail>(`summaries/${id}`),
    enabled: id !== null,
  });

export const useSummarySources = (id: number | null, enabled: boolean) =>
  useQuery({
    queryKey: ["summary", id, "sources"],
    queryFn: () => api<SummarySources>(`summaries/${id}/sources`),
    enabled: id !== null && enabled,
  });

export function useSummaryAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      body,
    }: {
      id: number;
      action: "approve" | "reject" | "reopen" | "regenerate-image" | "publish-now" | "edit";
      body?: unknown;
    }) =>
      action === "edit"
        ? api<SummaryDetail>(`summaries/${id}`, { method: "PATCH", body })
        : api<SummaryDetail>(`summaries/${id}/${action}`, { method: "POST", body }),
    onSuccess: (summary) => {
      client.setQueryData(["summary", summary.id], summary);
      client.invalidateQueries({ queryKey: ["summaries"] });
      client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

// --- Telegram -----------------------------------------------------------------

export const useTelegramStatus = () =>
  useQuery({
    queryKey: ["telegram", "status"],
    queryFn: () => api<{ configured: boolean }>("telegram/accounts/status"),
    staleTime: 5 * 60_000,
  });

export const useAccounts = () =>
  useQuery({
    queryKey: ["telegram", "accounts"],
    queryFn: () => api<TelegramAccount[]>("telegram/accounts"),
    refetchInterval: 30_000,
  });

export function useAccountMutations() {
  const invalidate = useInvalidate();
  const done = () => invalidate(["telegram"], ["overview"]);
  return {
    create: useMutation({
      mutationFn: (body: { phone: string; label?: string }) =>
        api<TelegramAccount>("telegram/accounts", { method: "POST", body }),
      onSuccess: done,
    }),
    sendCode: useMutation({
      mutationFn: (id: number) =>
        api<TelegramAccount>(`telegram/accounts/${id}/send-code`, { method: "POST" }),
      onSuccess: done,
    }),
    verify: useMutation({
      mutationFn: ({ id, ...body }: { id: number; code?: string; password?: string }) =>
        api<TelegramAccount>(`telegram/accounts/${id}/verify`, { method: "POST", body }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => api(`telegram/accounts/${id}`, { method: "DELETE" }),
      onSuccess: done,
    }),
  };
}

export const useChannels = () =>
  useQuery({
    queryKey: ["telegram", "channels"],
    queryFn: () => api<Channel[]>("telegram/channels"),
  });

export function useChannelMutations() {
  const invalidate = useInvalidate();
  const done = () => invalidate(["telegram"], ["overview"]);
  return {
    lookup: useMutation({
      mutationFn: (username: string) =>
        api<ChannelLookup>(`telegram/channels/lookup${query({ username })}`),
    }),
    add: useMutation({
      mutationFn: (body: { username: string; account?: number | null; join?: boolean }) =>
        api<Channel>("telegram/channels", { method: "POST", body }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: { id: number; is_active?: boolean; account?: number | null }) =>
        api<Channel>(`telegram/channels/${id}`, { method: "PATCH", body }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => api(`telegram/channels/${id}`, { method: "DELETE" }),
      onSuccess: done,
    }),
    backfill: useMutation({
      mutationFn: (id: number) =>
        api<{ detail: string }>(`telegram/channels/${id}/backfill`, { method: "POST" }),
      onSuccess: done,
    }),
  };
}

// --- AI -----------------------------------------------------------------------

export const useAIConfig = () =>
  useQuery({ queryKey: ["ai", "config"], queryFn: () => api<AIConfig>("ai/config") });

export function useUpdateAIConfig() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AIConfigUpdate) => api<AIConfig>("ai/config", { method: "PATCH", body }),
    onSuccess: (config) => {
      client.setQueryData(["ai", "config"], config);
      client.invalidateQueries({ queryKey: ["ai", "history"] });
      client.invalidateQueries({ queryKey: ["ai", "usage"] });
    },
  });
}

export const useAIHistory = () =>
  useQuery({
    queryKey: ["ai", "history"],
    queryFn: () => api<Paginated<AIConfigVersion>>("ai/config/history?page_size=20"),
  });

export const useTestPrompt = () =>
  useMutation({
    mutationFn: (hours: number) =>
      api<TestPromptResult>("ai/config/test-prompt", { method: "POST", body: { hours } }),
  });

export const useValidateKey = () =>
  useMutation({
    mutationFn: (apiKey?: string) =>
      api<KeyInfo & { valid: boolean }>("ai/config/validate-key", {
        method: "POST",
        body: apiKey ? { api_key: apiKey } : {},
      }),
  });

export const useModels = (q: string, minContext: number) =>
  useQuery({
    queryKey: ["ai", "models", q, minContext],
    queryFn: () => api<Catalog>(`ai/models${query({ q, min_context: minContext || undefined })}`),
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
  });

export function useRefreshModels() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api<{ count: number }>("ai/models/refresh", { method: "POST" }),
    onSuccess: () => invalidate(["ai", "models"], ["ai", "config"]),
  });
}

export const useUsage = (days: number) =>
  useQuery({
    queryKey: ["ai", "usage", days],
    queryFn: () => api<Usage>(`ai/usage?days=${days}`),
  });

// --- Publishing -----------------------------------------------------------------

export const usePlatforms = () =>
  useQuery({ queryKey: ["platforms"], queryFn: () => api<Platform[]>("platforms") });

export function usePlatformMutations() {
  const client = useQueryClient();
  const replace = (platform: Platform) =>
    client.setQueryData<Platform[]>(["platforms"], (old) =>
      old?.map((p) => (p.kind === platform.kind ? platform : p)),
    );
  return {
    update: useMutation({
      mutationFn: ({
        kind,
        ...body
      }: {
        kind: PlatformKind;
        is_enabled?: boolean;
        credentials?: Record<string, string>;
        config?: Record<string, string>;
      }) => api<Platform>(`platforms/${kind}`, { method: "PATCH", body }),
      onSuccess: replace,
    }),
    test: useMutation({
      mutationFn: (kind: PlatformKind) =>
        api<{ ok: boolean; message: string }>(`platforms/${kind}/test-connection`, {
          method: "POST",
        }),
      onSettled: () => client.invalidateQueries({ queryKey: ["platforms"] }),
    }),
  };
}

export interface JobFilters {
  status?: string;
  platform?: string;
  page?: number;
}

export const useJobs = (filters: JobFilters) =>
  useQuery({
    queryKey: ["jobs", filters],
    queryFn: () => api<Paginated<PublishJob>>(`jobs${query({ ...filters })}`),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

export function useJobMutations() {
  const invalidate = useInvalidate();
  const done = () => invalidate(["jobs"], ["overview"], ["summaries"], ["whatsapp"]);
  return {
    retry: useMutation({
      mutationFn: (id: number) => api<PublishJob>(`jobs/${id}/retry`, { method: "POST" }),
      onSuccess: done,
    }),
    markSent: useMutation({
      mutationFn: ({ id, url }: { id: number; url?: string }) =>
        api<PublishJob>(`jobs/${id}/mark-sent`, { method: "POST", body: { url } }),
      onSuccess: done,
    }),
  };
}

export const useWhatsAppExport = (days: number) =>
  useQuery({
    queryKey: ["whatsapp", days],
    queryFn: () => api<WhatsAppExport>(`platforms/whatsapp-export?days=${days}`),
  });

// --- Scheduling -----------------------------------------------------------------

export const useSchedules = () =>
  useQuery({ queryKey: ["schedules"], queryFn: () => api<Schedule[]>("schedules") });

export type ScheduleInput = Pick<
  Schedule,
  "name" | "kind" | "schedule_type" | "interval_minutes" | "cron" | "enabled" | "kwargs"
>;

export function useScheduleMutations() {
  const invalidate = useInvalidate();
  const done = () => invalidate(["schedules"]);
  return {
    create: useMutation({
      mutationFn: (body: ScheduleInput) => api<Schedule>("schedules", { method: "POST", body }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: Partial<ScheduleInput> & { id: number }) =>
        api<Schedule>(`schedules/${id}`, { method: "PATCH", body }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => api(`schedules/${id}`, { method: "DELETE" }),
      onSuccess: done,
    }),
    runNow: useMutation({
      mutationFn: (id: number) => api(`schedules/${id}/run-now`, { method: "POST" }),
      onSuccess: () => invalidate(["schedules"], ["overview"]),
    }),
  };
}

// --- Notifications ----------------------------------------------------------------

export const useNotificationSettings = () =>
  useQuery({
    queryKey: ["notifications", "settings"],
    queryFn: () => api<NotificationSettings>("notifications/settings"),
  });

export function useNotificationMutations() {
  const client = useQueryClient();
  return {
    update: useMutation({
      mutationFn: (body: Partial<NotificationSettings> & { smtp_password?: string }) =>
        api<NotificationSettings>("notifications/settings", { method: "PATCH", body }),
      onSuccess: (data) => client.setQueryData(["notifications", "settings"], data),
    }),
    testEmail: useMutation({
      mutationFn: (to?: string) =>
        api<{ ok: boolean; detail: string }>("notifications/settings/test-email", {
          method: "POST",
          body: to ? { to } : {},
        }),
    }),
  };
}

export const useAlertEvents = (filters: { type?: string; severity?: string; page?: number }) =>
  useQuery({
    queryKey: ["notifications", "events", filters],
    queryFn: () => api<Paginated<AlertEvent>>(`notifications/events${query({ ...filters })}`),
    placeholderData: keepPreviousData,
  });

export function useAlertMutations() {
  const invalidate = useInvalidate();
  const done = () => invalidate(["notifications", "events"], ["overview"]);
  return {
    markSeen: useMutation({
      mutationFn: (id: number) => api(`notifications/events/${id}/mark-seen`, { method: "POST" }),
      onSuccess: done,
    }),
    markAllSeen: useMutation({
      mutationFn: () => api("notifications/events/mark-all-seen", { method: "POST" }),
      onSuccess: done,
    }),
  };
}
